import { AspectRatio, AspectRatioDropdown, aspectRatioOptions } from '@/components/AspectRatioDropdown/AspectRatioDropdown';
import { FilterWithoutDividerOption } from '@/components/Voice/VoiceModal/SelectVoiceContent/FiltersSection/FilterChipSelect';
import { useAnalytics } from '@/services/analytics';
import { CropMode } from '@/utils/types';
import styled from '@emotion/styled';
import { SelectChangeEvent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdvancedEditorContext } from '../../context/useAdvancedEditorContext';
import { PolotnoStoreKeys, usePolotnoState } from '../../hooks/polotno-store';

const StyledAspectRatioDropdown = styled(AspectRatioDropdown)`
    .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary {
        border-radius: 9px !important;
        border: 0;

        color: ${({ theme }) => theme.colors.black[700]};
        font-size: 14px;
        font-weight: 500;
        line-height: 20px; /* 142.857% */
    }

    :hover,
    .is-open {
        border-radius: 9px !important;
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const getSelectedAspectRatio = (aspectRatio: AspectRatio | undefined, crop: CropMode) => {
    if (aspectRatio && AspectRatioToCropMode[aspectRatio] === crop) return aspectRatio;

    if (!crop || crop === CropMode.Fit) {
        return AspectRatio.Landscape;
    }

    if (crop === CropMode.Square) {
        return AspectRatio.Square;
    }

    if (crop === CropMode.Vertical) {
        return AspectRatio.Portrait;
    }

    return AspectRatio.Landscape;
};

const AspectRatioToCropMode: Record<AspectRatio, CropMode> = {
    [AspectRatio.Landscape]: CropMode.Wide,
    [AspectRatio.Square]: CropMode.Square,
    [AspectRatio.Portrait]: CropMode.Vertical,
    [AspectRatio.Youtube]: CropMode.Wide,
    [AspectRatio.YoutubeShort]: CropMode.Vertical,
    [AspectRatio.Facebook]: CropMode.Square,
    [AspectRatio.Instagram]: CropMode.Vertical,
    [AspectRatio.TikTok]: CropMode.Vertical,
};

export const CropModeToAspectRatio: Record<CropMode, AspectRatio> = {
    [CropMode.Wide]: AspectRatio.Landscape,
    [CropMode.Square]: AspectRatio.Square,
    [CropMode.Vertical]: AspectRatio.Portrait,
    [CropMode.Circle]: AspectRatio.Square,
    [CropMode.Fit]: AspectRatio.Landscape,
};

export const CropDropdown = observer(() => {
    const mixpanel = useAnalytics();
    const { editorInitialized } = useAdvancedEditorContext();
    const { state } = useLocation();

    const [cropSettings, setCropSettings] = usePolotnoState(PolotnoStoreKeys.cropSettings);
    const [aspectRatio, setAspectRatio] = usePolotnoState(PolotnoStoreKeys.aspectRatio);

    const [canvasLayoutReported, setCanvasLayoutReported] = useState(false);

    const aspectRatioFromLocation = aspectRatioOptions.find(option => option.value === state?.aspectRatio && option.value !== 'all');

    useEffect(() => {
        if (aspectRatioFromLocation && editorInitialized) {
            setAspectRatio(aspectRatioFromLocation.value as AspectRatio);
        }
    }, [aspectRatioFromLocation, editorInitialized]);

    const onMenuClick = () => {
        if (!canvasLayoutReported && mixpanel) {
            setCanvasLayoutReported(true);
            mixpanel.track('canvas-layout', { event: 'click' });
        }
    };

    const onSelect = (e: SelectChangeEvent<string>) => {
        setCropSettings(AspectRatioToCropMode[e.target.value as AspectRatio]);
        setAspectRatio(e.target.value as AspectRatio);
    };

    const defaultAspectRatio = aspectRatioOptions.find(option => option.value === AspectRatio.Landscape)!;

    const selectedAspectRatio =
        aspectRatioFromLocation ||
        aspectRatioOptions.find(option => option.value === getSelectedAspectRatio(aspectRatio, cropSettings)) ||
        defaultAspectRatio;

    const transformAspectRatio = (value: FilterWithoutDividerOption) => {
        return {
            ...value,
            chipLabel: value.secondaryLabel?.replaceAll('(', '').replaceAll(')', '') || value.chipLabel,
        };
    };

    return (
        <StyledAspectRatioDropdown
            selectedAspectRatio={selectedAspectRatio}
            onAspectRatioChange={onSelect}
            onOpen={onMenuClick}
            disabled={!editorInitialized}
            showAll={false}
            transformValue={transformAspectRatio}
        />
    );
});
